import { Box, Card, CircularProgress, ThemeProvider } from "@mui/material";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "../../utils/theme/theme";
import FilterBar from "../../components/filterBar/FilterBar";
import AgGridTrips from "./AgGridTrips";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import MobileCardTrips from "./MobileCardTrips";
import { useTripContext } from "../../context/tripsContext";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import { TripRequestBody, TripResponse } from "../../types/api";
import { tripsService } from "../../api/tripsService/tripsServices";
import { useNavigationType, useSearchParams } from "react-router-dom";
import useFleetInfo from "../../utils/hooks/useFleetInfo";
import { fleetService } from "../../api/fleetService/fleetServicesRegistered";
import { useSessionStorage } from "../../utils/hooks/useSessionStorage";

const Trips: React.FC = () => {
  const windowsWidth = useWindowSize();
  const isMobile = windowsWidth < 768;
  const { t } = useTranslation();
  const {
    businessProfile,
    loading: loadingProfile,
    error: profileError,
    legacyData,
  } = useBusinessProfile();
  const { trips, setTrips, isDataStale, setIsDataStale } = useTripContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigationType = useNavigationType();
  const [lastKpi, setLastKpi] = useState<string | null>(null);
  const [lastFilters, setLastFilters] = useState<TripRequestBody | null>(null);
  const [savedFilters, setSavedFilters] = useSessionStorage("tripsSavedFilters", null);
  const [filters, setFilters] = useState<TripRequestBody | null>(null); // Stato controllato dei filtri applicati
  const [pendingFilters, setPendingFilters] = useState<TripRequestBody | null>(null); // Stato dei filtri in fase di modifica
  const isInitialMount = useRef(true);
  const lastBusinessProfileRef = useRef(businessProfile);
  const lastLegacyDataRef = useRef(legacyData);
  const isFetchingRef = useRef(false);
  const lastTripsFetchRef = useRef<string>('');

  const { vehiclePlates, trailerPlates, loading, error } = useFleetInfo(businessProfile);

  // Stati per i driver
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(true);
  const [driversData, setDriversData] = useState<Record<string, boolean>>({});
  const [allDrivers, setAllDrivers] = useState<any[]>([]);
  const hasFetchedDriversRef = useRef(false);
  const businessProfileRef = useRef(businessProfile);
  const previousTripsLengthRef = useRef(trips.length);
  const lastKpiRef = useRef<string | null>(null);
  const lastTripsHashRef = useRef<string>('');
  const isFetchingDriversRef = useRef(false);
  
  const driversMap = useMemo(() => driversData, [driversData]);

  // Logica per caricare i driver (centralizzata nel componente padre)
  useEffect(() => {
    const fetchDrivers = async () => {
      if (!businessProfile?.legacycodes?.length) {
        setIsLoadingDrivers(false);
        hasFetchedDriversRef.current = true;
        return;
      }
      
      // Prevent concurrent fetches
      if (isFetchingDriversRef.current) {
        return;
      }
      
      const currentKpi = searchParams.get('kpi');
      
      // Create a hash of the current trips to detect meaningful changes
      const tripsHash = trips.map(trip => `${trip.tripID}-${trip.tractorLicensePlate}`).join('|');
      
      // Reset flag if business profile changed, trips changed significantly, or KPI changed
      // Only reset if we have actual trips to process
      if ((businessProfileRef.current !== businessProfile || 
          lastTripsHashRef.current !== tripsHash ||
          lastKpiRef.current !== currentKpi) && trips.length > 0) {
        hasFetchedDriversRef.current = false;
      }
      
      // Prevent multiple API calls for the same business profile, trips, and KPI
      if (hasFetchedDriversRef.current && 
          businessProfileRef.current === businessProfile && 
          lastTripsHashRef.current === tripsHash &&
          lastKpiRef.current === currentKpi) {
        setIsLoadingDrivers(false);
        return;
      }
      
      // Early return if no trips to process
      if (trips.length === 0) {
        setIsLoadingDrivers(false);
        hasFetchedDriversRef.current = true;
        return;
      }
      
      // Check if there are any trips with tractor license plates to process
      const hasTripsWithPlates = trips.some(trip => trip.tractorLicensePlate);
      if (!hasTripsWithPlates) {
        // Se non ci sono viaggi con targa, ferma il loading e non fare chiamate API
        setIsLoadingDrivers(false);
        hasFetchedDriversRef.current = true;
        return;
      }
      
      isFetchingDriversRef.current = true;
      setIsLoadingDrivers(true);
      const newDriversMap: Record<string, boolean> = {};
      const legacyCode = businessProfile.legacycodes[0].legacycode;
      const company = businessProfile.legacycodes[0].company;
      
      // Fetch all drivers using getDriversBySupplierCode
      try {
        const driversResponse = await fleetService.getDriversBySupplierCode(legacyCode, company);

        setAllDrivers(driversResponse as unknown as any[] || []);
        
        // Process each trip to check driver associations
        for (const trip of trips) {
          if (trip.tractorLicensePlate) {
            const licensePlate = trip.tractorLicensePlate;
            const tripStatus = trip.status;
            
            // Se il viaggio è in attesa di doc o completed, non fare controlli sui driver
            if (tripStatus === "WAITING FOR DOCUMENTATION" || tripStatus === "COMPLETED") {
              newDriversMap[licensePlate] = true; // Mostra normale (non in rosso/grassetto)
              continue;
            }
            
            // Per tutti gli altri stati, usare current date e controllare solo status "ACTIVE"
            const checkDate = new Date();
            
            // Check if any driver has an active association for this license plate
            const hasActiveAssociation = (driversResponse as unknown as any[])?.some((driver: any) => {
              
              return driver.associations?.some((association: any) => {
                // Check if association is for the correct license plate and is active
                if (association.vehicleLicensePlate === licensePlate && association.status === "ACTIVE") {
                  try {
                    const dateFrom = new Date(association.dateFrom);
                    const dateTo = association.dateTo ? new Date(association.dateTo) : null;
                    
                    // Check if dateFrom is valid
                    if (isNaN(dateFrom.getTime())) {
                      return false;
                    }
                    
                    // Check if dateTo is valid (if not null)
                    if (dateTo && isNaN(dateTo.getTime())) {
                      return false;
                    }
                    
                    // Check if the check date falls within the association date range
                    let isInRange: boolean;
                    if (dateTo) {
                      // If dateTo is not null, check if checkDate is between dateFrom and dateTo
                      isInRange = checkDate >= dateFrom && checkDate <= dateTo;
                    } else {
                      // If dateTo is null, check only if checkDate is >= dateFrom (no end date)
                      isInRange = checkDate >= dateFrom;
                    }
                    
                    return isInRange;
                  } catch (error) {
                    console.error(`Error parsing dates for association ${association.id}:`, error);
                    return false;
                  }
                }
                return false;
              });
            });
            
            newDriversMap[licensePlate] = hasActiveAssociation || false;
          }
        }
        
      } catch (error) {
        console.error('Error fetching all drivers:', error);
        setAllDrivers([]);
        
        // Set all plates to false if there's an error
        for (const trip of trips) {
          if (trip.tractorLicensePlate) {
            newDriversMap[trip.tractorLicensePlate] = false;
          }
        }
      }
      
      setDriversData(newDriversMap);
      setIsLoadingDrivers(false);
      hasFetchedDriversRef.current = true;
      businessProfileRef.current = businessProfile;
      previousTripsLengthRef.current = trips.length;
      lastKpiRef.current = currentKpi;
      lastTripsHashRef.current = tripsHash;
      isFetchingDriversRef.current = false;
    };

    // Only fetch drivers if we have trips and business profile
    if (trips.length > 0 && businessProfile?.legacycodes?.length) {
      fetchDrivers();
    }
  }, [trips, businessProfile, searchParams]);

  // Reset driver fetch flag when trips are loaded for the first time
  useEffect(() => {
    if (trips.length > 0 && !hasFetchedDriversRef.current) {
      // Reset the flag to allow driver fetch for new trips
      hasFetchedDriversRef.current = false;
    }
  }, [trips]);

  // Handle empty trips case - set isLoadingDrivers to false when trips are empty
  useEffect(() => {
    if (trips.length === 0 && isLoadingDrivers) {
      setIsLoadingDrivers(false);
    }
  }, [trips.length, isLoadingDrivers]);

  // Carica i filtri di default solo al primo mount (refresh)
  useEffect(() => {
    if (loadingProfile || !businessProfile) return;
    if (filters !== null) return; // Già inizializzato

    // Se ci sono filtri salvati in sessionStorage, usali
    if (savedFilters) {
      // Se ci sono filtri salvati, ignoriamo il KPI e usiamo i filtri salvati
      const kpi = searchParams.get('kpi');
      if (kpi === "1" || kpi === "2" || kpi === "3") {
        // Se c'è KPI ma abbiamo filtri salvati, rimuovi il KPI dai filtri salvati
        const filtersWithoutKpi = { 
          ...savedFilters, 
          kpi: undefined,
          dateType: 2 // Assicurati che sia expectedDeparture
        };
        setFilters(filtersWithoutKpi);
        setPendingFilters(savedFilters);
      } else {
        setFilters(savedFilters);
        setPendingFilters(savedFilters);
      }
      return;
    }

    // Altrimenti, carica i filtri di default
    const today = new Date();
    const dateFrom = new Date(today);
    dateFrom.setDate(today.getDate() - 15);
    dateFrom.setHours(0, 0, 0, 0);
    const dateTo = new Date(today);
    dateTo.setDate(today.getDate() + 15);
    dateTo.setHours(23, 59, 59, 999);

    const formatToLocalISOString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
    };

    const formatDateWithTimezone = (dateString: string | Date): string => {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZoneName: 'shortOffset'
      };
      const formatter = new Intl.DateTimeFormat('it-IT', options);
      const formatted = formatter.format(date);
      const timezoneMatch = formatted.match(/([+-]\d{2}:\d{2})$/);
      const offset = timezoneMatch ? timezoneMatch[1] : '+02:00';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`;
    };

    const kpi = searchParams.get('kpi');
    
    // Filtri di default sempre con le date (per la barra)
    const defaultFilters: TripRequestBody = {
      agencyqualification: businessProfile.agencyqualification ? formatDateWithTimezone(businessProfile.agencyqualification) : null,
      legacyCodes: legacyData,
      milestoneValue: ["ASSIGNED", "ALLOCATED", "IN PROGRESS", "WAITING FOR DOCUMENTATION"],
      approval: ["TO APPROVE", "APPROVED"],
      dateType: 2, // sempre expectedDeparture per la barra
      dateFrom: formatToLocalISOString(dateFrom),
      dateTo: formatToLocalISOString(dateTo),
    };
    
    // pendingFilters sempre con i filtri di default (con date)
    setPendingFilters(defaultFilters);
    
    // filters per le chiamate API (può includere KPI e date null)
    if (kpi === "1" || kpi === "2" || kpi === "3") {
      setFilters({ 
        ...defaultFilters, 
        dateType: 0, 
        kpi: parseInt(kpi, 10), 
        dateFrom: null, 
        dateTo: null 
      });
    } else {
      setFilters(defaultFilters);
    }
  }, [loadingProfile, businessProfile, legacyData, filters, savedFilters, searchParams]);

  // Quando filters cambia, fetch dei trips
  useEffect(() => {
    if (!filters) return;
    
    console.log('Trips: Filters changed, fetching new data');
    fetchAndSetTrips(filters);
  }, [filters, isDataStale]);

  // Navigazione avanti/indietro: ripristina i filtri da sessionStorage se non ci sono già
  useEffect(() => {
    if (navigationType === "POP" && !isDataStale && savedFilters && !filters) {
      console.log("Restoring saved filters for POP navigation");
      setFilters(savedFilters);
      setPendingFilters(savedFilters);
    }
  }, [navigationType, isDataStale, savedFilters, filters]);

  // Assicurati che pendingFilters sia sempre sincronizzato con i filtri salvati
  useEffect(() => {
    if (savedFilters && !pendingFilters) {
      console.log("Syncing pendingFilters with savedFilters");
      setPendingFilters(savedFilters);
    }
  }, [savedFilters, pendingFilters]);

  // Assicurati che i filtri salvati abbiano sempre le informazioni corrette per la barra
  useEffect(() => {
    if (savedFilters && pendingFilters) {
      // Se i filtri salvati non hanno date ma pendingFilters sì, aggiorna i filtri salvati
      if (!savedFilters.dateFrom && pendingFilters.dateFrom) {
        console.log("Updating savedFilters with pendingFilters dates");
        setSavedFilters(pendingFilters);
      }
    }
  }, [savedFilters, pendingFilters]);

  // Clear saved filters quando i dati diventano stale
  useEffect(() => {
    if (isDataStale) {
      setSavedFilters(null);
    }
  }, [isDataStale]);

  // Handle saved filters for POP navigation - usa i dati locali se disponibili
  useEffect(() => {
    if (navigationType === "POP" && !isDataStale && savedFilters) {
      // Se abbiamo già i trips nello state, non fare chiamate API
      if (trips.length > 0) {
        console.log("Using cached trips for POP navigation");
        // Assicurati che il loading sia fermato
        if (isLoading) {
          setIsLoading(false);
        }
        return;
      }
      
      // Solo se non abbiamo trips, applica i filtri salvati
      if (trips.length === 0) {
        console.log("Using saved filters for POP navigation");
        fetchAndSetTrips(savedFilters);
      }
    }
  }, [navigationType, isDataStale, savedFilters, trips.length, isLoading]);

  const formatDateWithTimezone = (dateString: string | Date): string => {
    const date = new Date(dateString);

    // Format the date in Italian locale to get the correct timezone
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'Europe/Rome',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'shortOffset'
    };

    const formatter = new Intl.DateTimeFormat('it-IT', options);
    const formatted = formatter.format(date);

    // Extract the timezone offset
    const timezoneMatch = formatted.match(/([+-]\d{2}:\d{2})$/);
    const offset = timezoneMatch ? timezoneMatch[1] : '+02:00';

    // Format the date in ISO format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`;
  };

  const fetchAndSetTrips = async (filters: TripRequestBody) => {
    console.log('Trips: fetchAndSetTrips called with filters:', filters);
    if (isFetchingRef.current) {
      console.log('Trips: Skipping fetch - already fetching');
      return;
    }
    
    // Create a hash of the filters to detect duplicate requests
    const filtersHash = JSON.stringify(filters);
    if (lastTripsFetchRef.current === filtersHash && !isDataStale) {
      console.log('Trips: Skipping fetch - same filters hash and data not stale');
      // Assicurati che il loading sia fermato
      if (isLoading) {
        setIsLoading(false);
      }
      return;
    }
    
    // Se stiamo navigando indietro e abbiamo già trips, non fare chiamate API
    if (navigationType === "POP" && trips.length > 0 && !isDataStale) {
      console.log('Trips: Skipping fetch for POP navigation with existing trips');
      if (isLoading) {
        setIsLoading(false);
      }
      return;
    }
    
    console.log('Trips: Starting fetch with filters:', filters);
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const response: TripResponse[] = await tripsService.fetchTrips(filters);
      setTrips(response);
      lastTripsFetchRef.current = filtersHash;
      console.log(`Trips: Successfully fetched ${response.length} trips`);
      
      // Only save filters if this is not an automatic KPI navigation
      // We'll use a flag to distinguish between automatic and manual filter applications
      const kpi = searchParams.get('kpi');
      const isAutomaticKpiNavigation = kpi && !savedFilters;
      
      if (!isAutomaticKpiNavigation) {
        // Only save filters that were manually applied, not from KPI navigation
        const filtersToSave = {
          ...filters,
          // Ensure we don't save KPI-related filters
          kpi: undefined
        };
        setSavedFilters(filtersToSave);
      }
    } catch (error) {
      console.error("Failed to fetch trips:", error);
    } finally {
      setIsLoading(false);
      setIsFiltering(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Early return if still loading or no business profile
    if (loadingProfile || !businessProfile) {
      return;
    }
    
    const kpi = searchParams.get('kpi');
    
    // Se stiamo tornando indietro e i dati non sono obsoleti, usa il cache
    if (navigationType === "POP" && !isDataStale) {
      console.log("Using cached data for POP navigation");
      // Assicurati che il loading sia fermato
      if (isLoading) {
        setIsLoading(false);
      }
      return;
    }
    
    // Controlla se businessProfile o legacyData sono realmente cambiati
    const businessProfileChanged = lastBusinessProfileRef.current !== businessProfile;
    const legacyDataChanged = lastLegacyDataRef.current !== legacyData;
    const kpiChanged = kpi !== lastKpi && lastKpi !== null;
    
    // Se il KPI è cambiato, marca i dati come obsoleti e forza il refetch
    if (kpiChanged) {
      console.log("KPI changed, marking data as stale and forcing refetch");
      setIsDataStale(true);
      // Don't clear saved filters when KPI changes - keep default filters intact
      // Only clear saved filters if there are no manually saved filters
      if (!savedFilters) {
        setSavedFilters(null);
      }
    }
    
    // Clear saved filters if business profile changed significantly
    if (businessProfileChanged) {
      setSavedFilters(null);
    }
    
    // Se non è il primo mount e nessuna dipendenza critica è cambiata e i dati non sono obsoleti, evita la chiamata
    if (!isInitialMount.current && !businessProfileChanged && !legacyDataChanged && !kpiChanged && !isDataStale) {
      console.log("No critical changes detected, skipping API call");
      // Assicurati che il loading sia fermato
      if (isLoading) {
        setIsLoading(false);
      }
      return;
    }
    
    // Aggiorna i ref per il prossimo controllo
    lastBusinessProfileRef.current = businessProfile;
    lastLegacyDataRef.current = legacyData;
    setLastKpi(kpi);
    isInitialMount.current = false;
    
    const today = new Date();
    const dateFrom = new Date(today);
    dateFrom.setDate(today.getDate() - 15);
    dateFrom.setHours(0, 0, 0, 0);
    const dateTo = new Date(today);
    dateTo.setDate(today.getDate() + 15);
    dateTo.setHours(23, 59, 59, 999);
    
    const formatToLocalISOString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Mesi da 0 a 11
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
    };

    const formattedDateFrom = formatToLocalISOString(dateFrom);
    const formattedDateTo = formatToLocalISOString(dateTo);

    const initialFilters: TripRequestBody = {
      agencyqualification: businessProfile.agencyqualification ? formatDateWithTimezone(businessProfile.agencyqualification) : null,
      legacyCodes: legacyData,
      milestoneValue: ["ASSIGNED", "ALLOCATED", "IN PROGRESS", "WAITING FOR DOCUMENTATION"],
      approval: ["TO APPROVE", "APPROVED"],
      dateType: kpi ? 0 : 2,
      dateFrom: kpi ? null : formattedDateFrom,
      dateTo: kpi ? null : formattedDateTo,
      kpi:
        kpi === "1" || kpi === "2" || kpi === "3"
          ? parseInt(kpi, 10)
          : undefined,
    };

    console.log("Fetching trips with initial filters:", initialFilters);
    fetchAndSetTrips(initialFilters).then(() => {
      setIsDataStale(false);
    });
  }, [loadingProfile, businessProfile, legacyData, navigationType, isDataStale, searchParams, isLoading]);

  // Funzione per applicare i filtri manualmente
  const handleFilterApply = (newFilters: TripRequestBody) => {
    console.log('Trips: Applying filters manually:', newFilters);
    
    // Attiva immediatamente il filtering state per nascondere la grid
    setIsFiltering(true);
    
    // Quando l'utente applica filtri manualmente, ignoriamo il KPI e usiamo i filtri della barra
    // I filtri manuali hanno sempre priorità sul KPI
    const filtersForApi = { 
      ...newFilters, 
      dateType: 2, // Sempre expectedDeparture per filtri manuali
      kpi: undefined // Rimuovi il KPI quando si applicano filtri manuali
    };
    
    console.log('Trips: Filters for API (manual override):', filtersForApi);
    
    // Attiva il loading
    setIsLoading(true);
    setIsDataStale(true);
    
    // Chiama direttamente fetchAndSetTrips per evitare problemi di timing
    fetchAndSetTrips(filtersForApi);
    
    setFilters(filtersForApi); // Applica i filtri per l'API
    setPendingFilters(newFilters); // Mantieni i filtri originali per la barra
    setSavedFilters(newFilters); // Salva sempre i filtri manuali con tutte le informazioni della barra
  };


  if (loadingProfile || !businessProfile) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100vh" }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: "100%" }}>
        <div className="p-6 md:p-8">
          <h1 className="mb-1 text-[32px] font-bold leading-[37.5px]">
            {t("screens.trips.heading")}
          </h1>
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: isMobile ? "flex-start" : "flex-end",
            paddingRight: "8px",
          }}
        >
          <FilterBar 
            filters={pendingFilters}
            setFilters={setPendingFilters}
            onFilterApply={handleFilterApply} 
          />
        </div>
      </Box>

      <Box>
        {isLoading || isFiltering ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "100%" }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : !isMobile ? (
          <Card elevation={3} sx={{ margin: "24px 24px 0px 24px" }}>
            <AgGridTrips 
              trips={trips} 
              vehiclePlateList={vehiclePlates} 
              trailerPlateList={trailerPlates}
              driversMap={driversMap}
              isLoadingDrivers={isLoadingDrivers}
              isLoading={isLoading || isFiltering}
            />
          </Card>
        ) : (
          <MobileCardTrips 
            trips={trips}
            driversMap={driversMap}
            isLoadingDrivers={isLoadingDrivers}
            isLoading={isLoading || isFiltering}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default Trips;
